import Form from "react-bootstrap/Form";
import "./addproductmaster.scss";
import { Button } from "react-bootstrap";
import * as Yup from "yup";
import Select from 'react-select';
import { FormikHelpers, useFormik } from "formik";
import useProducts from "../../../Hooks/useProduct";
import { useLocation, useNavigate } from "react-router-dom";
import { selectCurrentUserId } from "../../../redux/auth/authSlice";
import { useAppSelector } from "../../../redux/hooks";
import Loading from "../../../components/LoadingPage/Loading";
import { useEffect, useState } from "react";
import { Product, selectedProduct } from "../../../redux/product/productSlice";
import { CharacterLength } from "../../../Hooks/constants";
import { useModulePermissions, PermissionModule, PermissionIdentifier } from "../../../utils/permissionUtils";

const MVXStatusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Retired', label: 'Retired' }
];

const AddProductMaster = () => {
  const productMasterUrl = "/product-master";
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isDuplicate = searchParams.get("isDuplicate") === "true";
  const { createProductAPI, createProductLoading } = useProducts();
  const { editProductAPI, editProductLoading } = useProducts();
  const { duplicateProductAPI, duplicateProductLoading } = useProducts();
  const [isEdit, setIsEdit] = useState(false);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const selectedProductData = useAppSelector<Product | null>(selectedProduct);
  const [dynamicVal, setDynamicVal] = useState({
    productCode: "",
    barcode: "",
    productName: "",
    category: "",
    manufacturer: "",
    model: "",
    mvxStatus: "",
    mvxCode: "",
    mvxManufacturer: "",
    isMedicine: false,
    isActive: true,
    createdBy: currentUserId,
  });
  useEffect(() => {
    if (location.pathname === "/edit-product-master") {
      if (!isDuplicate) {
        setIsEdit(true);
      }
      if (selectedProductData?.id) {
        const prefillData = {
          productCode: selectedProductData.productCode,
          barcode: selectedProductData.barcode,
          productName: selectedProductData.productName,
          category: selectedProductData.category,
          manufacturer: selectedProductData.manufacturer,
          model: selectedProductData.model,
          isMedicine: selectedProductData.isMedicine,
          isActive: true,
          createdBy: currentUserId,
          mvxCode: selectedProductData?.mvxCode ?? "",
          mvxManufacturer: selectedProductData?.mvxManufacturer ?? "",
          mvxStatus: selectedProductData?.mvxStatus ?? "",
        };
        if (isDuplicate) {
          setDynamicVal({ ...prefillData, productCode: "", barcode: "" });
        } else {
          setDynamicVal(prefillData);
        }
      } else {
        navigate(productMasterUrl);
      }
    }
  }, []);
  const { handleChange, values, handleSubmit, touched, errors, setFieldValue } = useFormik({
    initialValues: dynamicVal,
    validationSchema: Yup.object({
      productCode: Yup.string()
        .required("Product Code is required")
        .max(
          CharacterLength.fifty,
          "Product Code should not be more than 50 characters"
        ),
      productName: Yup.string()
        .required("Product Name is required")
        .max(
          CharacterLength.fiveHundred,
          "Product Name should not be more than 500 characters"
        ),
      barcode: Yup.string()
        .max(75, "Barcode should not be more than 75 characters")
        .when("isMedicine", {
          is: false,
          then: (schema) => schema.required("Barcode is required"),
        }),
      manufacturer: Yup.string()
        .required("Manufacturer is required")
        .max(
          CharacterLength.twoFiveSix,
          "Manufacturer should not be more than 256 characters"
        ),
      category: Yup.string()
        .max(
          CharacterLength.oneTwoFive,
          "Category should not be more than 125 characters"
        )
        .when("isMedicine", {
          is: false,
          then: (schema) => schema.required("Category is required"),
        }),
      model: Yup.string()
        .max(
          CharacterLength.hundred,
          "Model should not be more than 100 characters"
        )
        .when("isMedicine", {
          is: false,
          then: (schema) => schema.required("Model is required"),
        }),
    }),
    onSubmit: async (values: any, { setSubmitting }: FormikHelpers<any>) => {
      try {
        if (isDuplicate) {
          await duplicateProductAPI({
            ...values,
            id: selectedProductData?.id,
            updatedBy: currentUserId ?? "",
          });
        } else if (isEdit) {
          await editProductAPI({ ...values, id: selectedProductData?.id });
        } else {
          await createProductAPI(values);
        }
        navigate(productMasterUrl);
      } catch (err) { }
      setSubmitting(false);
    },
    enableReinitialize: true,
  });

  const isPermitted = useModulePermissions(PermissionModule.PRODUCT_MASTER);
  const isSubmitPermitted = isPermitted(PermissionIdentifier.CREATE_PRODUCT_MASTER) || isPermitted(PermissionIdentifier.EDIT_PRODUCT_MASTER);

  return (
    <div className="add-product-master-wrapper">
      {(createProductLoading || editProductLoading || duplicateProductLoading) && <Loading />}
      <div className="page-title">
        <h2>{isDuplicate ? "Duplicate" : isEdit ? "Edit" : "Add"} Product</h2>
      </div>
      <div className="add-product-master-box">
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>
              Product Code / NDC<sup>*</sup>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Product Code"
              id="productCode"
              name="productCode"
              onChange={handleChange}
              value={values.productCode}
              isInvalid={touched.productCode && !!errors.productCode}
            />
            {touched.productCode && errors.productCode ? (
              <Form.Control.Feedback type="invalid">
                {errors.productCode}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Barcode{!values.isMedicine && <sup>*</sup>}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Barcode"
              id="barcode"
              name="barcode"
              onChange={handleChange}
              value={values.barcode}
              isInvalid={touched.barcode && !!errors.barcode}
            />
            {touched.barcode && errors.barcode ? (
              <Form.Control.Feedback type="invalid">
                {errors.barcode}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Product Name<sup>*</sup>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Product Name"
              id="productName"
              name="productName"
              onChange={handleChange}
              value={values.productName}
              isInvalid={touched.productName && !!errors.productName}
            />
            {touched.productName && errors.productName ? (
              <Form.Control.Feedback type="invalid">
                {errors.productName}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Category / RXGroup{!values.isMedicine && <sup>*</sup>}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Category"
              id="category"
              name="category"
              onChange={handleChange}
              value={values.category}
              isInvalid={touched.category && !!errors.category}
            />
            {touched.category && errors.category ? (
              <Form.Control.Feedback type="invalid">
                {errors.category}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Manufacturer<sup>*</sup>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Manufacturer"
              id="manufacturer"
              name="manufacturer"
              onChange={handleChange}
              value={values.manufacturer}
              isInvalid={touched.manufacturer && !!errors.manufacturer}
            />
            {touched.manufacturer && errors.manufacturer ? (
              <Form.Control.Feedback type="invalid">
                {errors.manufacturer}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Model / CVX{!values.isMedicine && <sup>*</sup>}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Model"
              id="model"
              name="model"
              onChange={handleChange}
              value={values.model}
              isInvalid={touched.model && !!errors.model}
            />
            {touched.model && errors.model ? (
              <Form.Control.Feedback type="invalid">
                {errors.model}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>MVX Code</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter MVX Code"
              id="mvxCode"
              name="mvxCode"
              onChange={handleChange}
              value={values.mvxCode}
              isInvalid={touched.mvxCode && !!errors.mvxCode}
            />
            {touched.mvxCode && errors.mvxCode ? (
              <Form.Control.Feedback type="invalid">
                {errors.mvxCode}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>MVX Manufacturer</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter MVX Manufacturer"
              id="mvxManufacturer"
              name="mvxManufacturer"
              onChange={handleChange}
              value={values.mvxManufacturer}
              isInvalid={touched.mvxManufacturer && !!errors.mvxManufacturer}
            />
            {touched.mvxManufacturer && errors.mvxManufacturer ? (
              <Form.Control.Feedback type="invalid">
                {errors.mvxManufacturer}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>MVX Status</Form.Label>
            <Select
              name='mvxStatus'
              id="mvxStatus"
              options={MVXStatusOptions}
              classNamePrefix="react-select"
              placeholder="Enter MVX Status"
              onChange={(selectedItem) => setFieldValue("mvxStatus", selectedItem?.value)}
              value={MVXStatusOptions.find(item => item.value === values.mvxStatus) || null}
              className={`${touched.mvxStatus && errors.mvxStatus ? "is-invalid-border" : ""} react-select-container`}
            />
            {touched.mvxStatus && errors.mvxStatus ? (
              <Form.Control.Feedback type="invalid">
                {errors.mvxStatus}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-3">
            <div className="item-checkbox-content">
              <Form.Check
                inline
                label="IsMed"
                type="checkbox"
                id="isMedicine"
                name="isMedicine"
                onChange={handleChange}
                checked={values.isMedicine}
              />
            </div>
          </Form.Group>
          <Form.Group className="mb-3 full-width text-end">
            <Button
              style={{ marginRight: "10px" }}
              variant="outline-primary"
              onClick={() => navigate(productMasterUrl)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isSubmitPermitted}>
              {isDuplicate ? "Duplicate" : isEdit ? "Update" : "Add"}
            </Button>
          </Form.Group>
        </Form>
      </div>
    </div>
  );
};

export default AddProductMaster;
